import { allCountries } from 'country-telephone-data';
import { useEffect, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { TextField } from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';
import {
    ContactType,
    ContactTypeField,
} from '@components/molecules/ContactTypeField/ContactTypeField';

import { UpdateUserDetailsDocument } from '@graphql/generated/graphql';

import { useUserBasic } from '@utils/hooks/useUserBasic';

import { getPath } from '@services/RouteService/paths';

import { useMutation } from '@apollo/client';
import { alreadyFilledOutPersonalInfoVar } from '@boot/reactiveVariables';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    emailValidation,
    firstNameValidation,
    lastNameValidation,
    phoneValidation,
} from '@shared/validations';

interface ChangePersonalInfoFormProps {
    callback?: () => void;
    disclosureUuid: string;
    setIsDisabled?: (value: boolean) => void;
    setIsLoading?: (value: boolean) => void;
}

export const CHANGE_PERSONAL_INFO_FORM_ID = 'changePersonalInfoForm';

export const ChangePersonalInfoForm = ({
    callback,
    disclosureUuid,
    setIsDisabled,
    setIsLoading,
}: ChangePersonalInfoFormProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { data, loading: dataLoading } = useUserBasic();

    const [contactType, setContactType] = useState<ContactType>('UNSET');

    const [updateUserDetails, { loading }] = useMutation(
        UpdateUserDetailsDocument,
        {
            context: {
                notifyOnError: true,
            },
            onCompleted: () => {
                disclosureUuid &&
                    navigate(
                        getPath({
                            params: {
                                disclosureUuid,
                            },
                            path: '/disclosure/details/:disclosureUuid',
                        }),
                    );

                reset();
                callback?.();
            },
            onError: () => {},
        },
    );

    const {
        control,
        formState: { errors, isSubmitted, isValid },
        handleSubmit,
        register,
        reset,
    } = useForm<ChangePersonalInfoFormValues>({
        mode: 'onTouched',
        resolver: yupResolver(
            getChangePersonalInfoFormSchema(contactType === 'PHONE'),
        ),
    });

    const { field: contactField } = useController({
        control,
        name: 'contact',
    });

    const onSubmit = handleSubmit(async (payload) => {
        const isPhone = contactType === 'PHONE';

        alreadyFilledOutPersonalInfoVar(true);

        await updateUserDetails({
            variables: {
                userDetailsInput: {
                    contactEmail: !isPhone
                        ? payload.contact.contactEmail
                        : undefined,
                    countryCode: isPhone
                        ? payload.contact.countryCode?.replace('+', '')
                        : undefined,
                    firstName: payload.firstName,
                    lastName: payload.lastName,
                    phoneNumber: isPhone
                        ? payload.contact.phoneNumber?.replace(/ /g, '')
                        : undefined,
                },
            },
        });
    });

    useEffect(() => {
        if (!dataLoading && data?.user) {
            const {
                contactEmail,
                country,
                firstName,
                lastName,
                phoneCountryPrefix,
                phoneNonPrefixed,
                phoneNumber,
            } = data.user;

            // NOTE: set default way of contact
            let contactType: ContactType = 'UNSET';
            if (contactEmail) contactType = 'EMAIL';
            else if (phoneNumber) contactType = 'PHONE';

            setContactType(contactType);

            // NOTE: set phone details based on user country
            const countryData = allCountries.find(
                ({ iso2 }) => iso2 === country?.toLowerCase(),
            );
            const countryCode =
                phoneCountryPrefix || `+${countryData?.dialCode}`;

            reset({
                contact: {
                    contactEmail: contactEmail || '',
                    countryCode,
                    phoneNumber: phoneNonPrefixed || '',
                },
                firstName: firstName || '',
                lastName: lastName || '',
            });
        }
    }, [data, dataLoading]);

    useEffect(() => {
        setIsDisabled?.(contactType === 'UNSET' || (!isValid && isSubmitted));
    }, [contactType, isValid, isSubmitted]);

    useEffect(() => {
        setIsLoading?.(loading);
    }, [loading]);

    return (
        <StandardForm id={CHANGE_PERSONAL_INFO_FORM_ID} onSubmit={onSubmit}>
            <TextField
                {...register('firstName')}
                className="htmlToImageIgnore"
                error={!!errors.firstName}
                label={t('FIRST_NAME')}
                message={t(errors.firstName?.message || '')}
                placeholder={t('FIRST_NAME_PLACEHOLDER')}
                testId="coIk7BO"
            />
            <TextField
                {...register('lastName')}
                className="htmlToImageIgnore"
                error={!!errors.lastName}
                label={t('LAST_NAME')}
                message={t(errors.lastName?.message || '')}
                placeholder={t('LAST_NAME_PLACEHOLDER')}
                testId="FOZkWLL"
            />
            <ContactTypeField
                contactType={contactType}
                errors={{
                    contactEmail: errors.contact?.contactEmail?.message,
                    countryCode: errors.contact?.countryCode?.message,
                    phoneNumber: errors.contact?.phoneNumber?.message,
                }}
                onChange={contactField.onChange}
                setContactType={setContactType}
                value={contactField.value}
            />
        </StandardForm>
    );
};

const getChangePersonalInfoFormSchema = (validationWithPhone?: boolean) =>
    yup.object({
        contact: yup.object({
            contactEmail: validationWithPhone
                ? emailValidation.optional().nullable()
                : emailValidation.nullable(),
            countryCode: validationWithPhone
                ? yup.string().required('FIELD_CANNOT_BE_EMPTY').nullable()
                : yup.string().optional().nullable(),
            phoneNumber: validationWithPhone
                ? phoneValidation.nullable()
                : yup.string().optional().nullable(),
        }),
        firstName: firstNameValidation,
        lastName: lastNameValidation,
    });

export type ChangePersonalInfoFormValues = yup.InferType<
    ReturnType<typeof getChangePersonalInfoFormSchema>
>;
